import { memo } from 'react';

import { TaskFilterOptions, TaskFilters } from '@/forms/TaskFilterForm/types';
import { ItemStatus } from '@/types/item';
import SharedActiveFilters from '@/ui/components/ActiveFilters';
import buildActiveFilterGroups from '@/utils/buildActiveFilterGroups';
import formatStaffName from '@/utils/formatStaffName';
import itemStatusName from '@/utils/itemStatusName';
import removeFilter from '@/utils/removeFilter';

interface Props {
  filterOptions: TaskFilterOptions;
  filters: Partial<TaskFilters>;
  setFilters: (filters: Partial<TaskFilters>) => void;
  values: Partial<TaskFilters>;
}

const FILTER_LABELS = {
  assigned_user_id: 'Assigned User',
  assigned_work_group_id: 'Assigned Work Group',
  blueprint_id: 'Task Type',
  due_at_end: 'Due To',
  due_at_start: 'Due From',
  search: 'Search',
  status: 'Status'
};

const ActiveFilters = ({ filterOptions, filters, setFilters, values }: Props) => {
  const getAssignedUserName = (assignedUserId: string): string => {
    const staffUser = filterOptions.staffUsers.find((staff) => staff.id === assignedUserId);

    return staffUser ? formatStaffName({ staff: staffUser }) : '';
  };

  const getAssignedWorkGroupName = (assignedWorkGroupId: string): string =>
    filterOptions.workGroups.find((workGroup) => workGroup.id === assignedWorkGroupId)?.name ?? '';

  const getBlueprintName = (blueprintId: string): string =>
    filterOptions.blueprints.find((blueprint) => blueprint.id === blueprintId)?.name ?? '';

  const formatFilterValue = (value: string, filterName: string) => {
    switch (filterName) {
      case 'due_at_start':
      case 'due_at_end':
        return new Date(value).toLocaleString([], { dateStyle: 'medium' });
      case 'assigned_user_id':
        return getAssignedUserName(value);
      case 'assigned_work_group_id':
        return getAssignedWorkGroupName(value);
      case 'blueprint_id':
        return getBlueprintName(value);
      case 'status':
        return itemStatusName(value as ItemStatus);
      default:
        return value;
    }
  };

  return (
    <SharedActiveFilters
      groups={buildActiveFilterGroups({
        filters,
        formatValue: formatFilterValue,
        hiddenFilters: ['ordering', 'page'],
        labels: FILTER_LABELS
      })}
      onClearAll={() => setFilters({})}
      onRemove={(filterName, filterValue) =>
        removeFilter({ activeFilters: filters, filterName, filterValue, setFilters, values })
      }
    />
  );
};

export default memo(ActiveFilters);
